import axios from "axios";
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  addDoc,
  serverTimestamp,
  getDoc,
  updateDoc,
  setDoc,
} from "@firebase/firestore";
import db from "services/firebase";
import { showErrorToast, showInfoToast, showSuccessToast, showLoadingToast, updateLoadingToast, dismissLoadingToast } from "app/components/ShowToast";

interface JiraIssue {
  id: string;
  key: string;
  fields: {
    summary?: string;
    status?: { name: string };
    issuetype?: { name: string };
    project?: { key: string };
    created?: string;
    updated?: string;
  };
}

interface JiraResponse {
  success: boolean;
  issues?: JiraIssue[];
  error?: string;
  taskIssues?: string[];
}

interface TaskData {
  count: number;
  tasks: Array<{
    issue_key: string;
    summary: string;
    status: string;
    project_key: string;
    created: string;
    updated: string;
  }>;
  last_checked: string;
  status: "active" | "access_issue" | "no_tasks";
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
  self: string;
}

export const createJiraService = (accessToken: string, cloudId: string) => {
  const apiBaseUrl = `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3`;

  const axiosInstance = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  const checkJiraTasks = async (projectKey: string): Promise<JiraResponse> => {
    try {
      const response = await axiosInstance.get("/search", {
        params: {
          jql: `project = ${projectKey} AND issuetype = Task`,
          maxResults: 100,
          fields: "summary,status,issuetype,project,created,updated",
        },
      });

      return {
        success: true,
        issues: response.data.issues,
      };
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.data?.errorMessages?.includes("Insufficient permissions")) {
        return {
          success: false,
          error: "Insufficient permissions for project",
          taskIssues: [projectKey],
        };
      }
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  };



  const saveJiraTasks = async (
    tenantId: string,
    projectKey: string,
    issues: JiraIssue[] | null,
    status: "active" | "access_issue" | "no_tasks",
    integrationId: string
  ) => {
    try {
      const taskData: TaskData = {
        count: issues ? issues.length : 0,
        tasks: issues
          ? issues.map((issue) => ({
              issue_key: issue.key,
              summary: issue.fields.summary || "No summary",
              status: issue.fields.status?.name || "Unknown",
              project_key: issue.fields.project?.key || projectKey,
              created: issue.fields.created || new Date().toISOString(),
              updated: issue.fields.updated || new Date().toISOString(),
            }))
          : [],
        last_checked: new Date().toISOString(),
        status,
      };

      const tenantRef = doc(db, "tenants", tenantId);
      const tasksRef = collection(tenantRef, "tasklists");
      const batch = writeBatch(db);

      if (status === "active" && issues) {
        for (const task of taskData.tasks) {
          const existingTaskQuery = query(
            tasksRef,
            where("issue_key", "==", task.issue_key),
            where("project_key", "==", projectKey)
          );
          const existingTaskSnapshot = await getDocs(existingTaskQuery);

          if (existingTaskSnapshot.empty) {
            batch.set(doc(tasksRef), {
              ...task,
              source: "Jira",
              created_at: new Date().toISOString(),
              scan_date: taskData.last_checked,
            });
          } else {
            const existingDoc = existingTaskSnapshot.docs[0];
            batch.update(doc(tasksRef, existingDoc.id), {
              scan_date: taskData.last_checked,
              status: task.status,
              updated: task.updated,
            });
          }
        }
      } else if (status === "access_issue") {
        // No tasks saved for projects with access issues
      } else if (status === "no_tasks") {
        const existingTaskQuery = query(
          tasksRef,
          where("project_key", "==", projectKey),
          where("status", "==", "access_issue")
        );
        const existingTaskSnapshot = await getDocs(existingTaskQuery);
        existingTaskSnapshot.forEach((docSnap) => {
          batch.delete(doc(tasksRef, docSnap.id));
        });
      }

      const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
      batch.update(integrationRef, {
        last_scan: new Date().toISOString(),
      });

      await batch.commit();
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error",
      };
    }
  };

  const scanProjectsForTasks = async (
    projects: Array<{
      key: string;
      name: string;
      url: string;
    }>,
    tenantId: string,
    integrationId: string
  ) => {
    try {
      let taskCount = 0;
      const taskIssues: string[] = [];
      const batch = writeBatch(db);

      for (const project of projects) {
        const result = await checkJiraTasks(project.key);

        if (result.success && result.issues) {
          const status = result.issues.length > 0 ? "active" : "no_tasks";
          taskCount += result.issues.length;
          const saveResult = await saveJiraTasks(
            tenantId,
            project.key,
            result.issues,
            status,
            integrationId
          );
          if (!saveResult.success) {
            console.error(`Failed to save tasks for ${project.key}:`, saveResult.error);
          }
        } else if (result.taskIssues) {
          taskIssues.push(...result.taskIssues);
          const saveResult = await saveJiraTasks(
            tenantId,
            project.key,
            null,
            "access_issue",
            integrationId
          );
          if (!saveResult.success) {
            console.error(`Failed to save access issue for ${project.key}:`, saveResult.error);
          }
        }
      }

      if (taskIssues.length > 0) {
        const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
        batch.update(integrationRef, {
          task_issues: taskIssues,
          last_scan: new Date().toISOString(),
        });
        await batch.commit();
      }

      if (taskIssues.length > 0) {
        showInfoToast({
          message: `Scan completed: ${taskCount} tasks found in ${
            projects.length - taskIssues.length
          } projects. Access issues detected for ${taskIssues.length} projects.`,
        });
        return {
          success: false,
          error: "Access issues detected for some projects",
          taskIssues,
          totalTasks: taskCount,
          scannedProjects: projects.length,
        };
      }

      showSuccessToast({
        message: `Task scan completed. Found ${taskCount} tasks across ${projects.length} projects.`,
      });

      return {
        success: true,
        totalTasks: taskCount,
        scannedProjects: projects.length,
      };
    } catch (error: any) {
      const errorMessage = error.message || "Unknown error";
      showErrorToast({ message: "Failed to scan tasks" });
      return {
        success: false,
        error: errorMessage,
        taskIssues: [],
        totalTasks: 0,
        scannedProjects: projects.length,
      };
    }
  };

  return {
    checkJiraTasks,
    createJiraTask,
    saveJiraTasks,
    scanProjectsForTasks,
  };
};

export const createJiraTask = async (
  projectKey: string,
  summary: string,
  description: string,
  priority?: string,
  dueDate?: string,
  accessToken?: string,
  cloudId?: string,
  assigneeId?:string
): Promise<JiraResponse> => {
  try {
    const response = await axios.post(
      'https://us-central1-slate-134a0.cloudfunctions.net/jiraCreateTask',
      {
        projectKey,
        summary,
        description,
        accessToken,
        cloudId,
        assigneeId
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to create task'
    };
  }
};

export const exchangeCodeForJiraToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void,
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: 'Authenticating with Jira...' });
    
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/jiraOAuthCallback?code=${encodeURIComponent(code)}`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json().catch(() => ({}));
      throw new Error(errorData.details || `Server responded with status: ${tokenResponse.status}`);
    }

    const responseText = await tokenResponse.text();
    let data;
    
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error('Invalid response format from server');
    }

    if (!data.access_token || typeof data.access_token !== 'string' || data.access_token.trim() === '') {
      console.error('Invalid access token in response:', data);
      throw new Error('No valid access token received from server');
    }

    updateLoadingToast(toastId, {
      message: 'Token received, fetching projects...',
      type: 'info',
      isLoading: true,
    });

    const integrationId = await fetchAndSaveJiraProjects(
      data.access_token,
      data.resources,
      data.projects,
      data.users,
      currentUser,
      navigate
    );

    dismissLoadingToast(toastId);
    showSuccessToast({ message: 'Successfully connected to Jira' });
    navigate(`/integrations?id=${integrationId}&service=Jira`, { replace: true });
  } catch (error: any) {
    console.error('Jira authentication error:', error);
    showErrorToast({ 
      message: error.message || 'Jira authentication failed. Please try again!' 
    });
    dismissLoadingToast(toastId);
    localStorage.removeItem('jira_access_token');
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export const fetchAndSaveJiraProjects = async (
  accessToken: string,
  resources: any,
  projects: any[],
  users: any[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void,
) => {
  try {
    const cloudId = resources[0].id;

    const tenantRef = doc(db, "tenants", currentUser?.tenantId);
    const jiraRef = collection(tenantRef, "integrations");
    const vendorsRef = collection(tenantRef, "vendors");
    const accessReviewRef = doc(tenantRef, 'access_review', 'jira');
    const integrationSnapshot = await getDocs(
      query(jiraRef, where("name", "==", "Jira"))
    );

    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

    if (integrationSnapshot.empty) {
      const newData = {
        accessToken,
        name: "Jira",
        group: "Task Management and Issue Tracking",
        img_path: "/media/integrations/Jira.svg",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        cloudId,
        resources,
        projects: projects.map(project => ({
          id: project.id,
          key: project.key,
          name: project.name,
          url: project.self
        })),
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
      };
    
      const vendorData = {
        authentication_type: "OAuth",
        category: "Task Management and Issue Tracking",
        created_at: serverTimestamp(),
        due_deligence_status: "Incomplete",
        impact_assessment: [],
        logo: [{
          name: "jira_logo.png",
          size: 3395,
          url: "/media/integrations/Jira.svg"
        }],
        owner: currentUser?.userName || "",
        policy_url: "https://www.atlassian.com/legal/privacy-policy",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://www.atlassian.com/legal/cloud-terms-of-service",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "Jira",
        website_url: "https://www.atlassian.com/software/jira"
      };

      const batch = writeBatch(db);
      await addDoc(jiraRef, newData);
      await addDoc(vendorsRef, vendorData);
      await batch.commit();
    }

    // Save users to access review collection
    const membersMap: { [key: string]: any } = {};
    for (const user of users || []) {
      if (user.accountId) {
        membersMap[user.accountId] = {
          ...user,
          name: user.displayName,
          platform: 'Jira',
          tenantId: currentUser.tenantId,
          uid: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'active',
        };
      }
    }

    const jiraDoc = await getDoc(accessReviewRef);
    if (jiraDoc.exists()) {
      await updateDoc(accessReviewRef, {
        members: {
          ...jiraDoc.data().members,
          ...membersMap,
        },
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(accessReviewRef, {
        platform: 'Jira',
        members: membersMap,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error: any) {
    showErrorToast({ message: 'Error fetching Jira projects' });
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export default createJiraService;